// The NES master palette as a set of named Munos constants.
//
// The PPU's 64-entry master palette maps each 6-bit color index ($00-$3F)
// to an RGB output. We expose the most useful entries as `NES_*` u32
// constants so Munos scripts can compose palettes without remembering
// raw hex codes.
//
// Source palette: the NesJs core's own NTSC table — a VERBATIM copy of
// `this.nesPal` in `packages/cores/nes-nesjs/src/vendor/nes/pipu.js`
// (the `[r, g, b]` triples indexed 0..63). This is the table that
// actually renders on-screen, so what NES_RED resolves to here matches
// the pixel the core paints at the same master index. (Previously this
// file used the JSNES NTSC table, which drifted from the real core — the
// two disagree on, e.g., $20 vs $30. NesJs is the single source of truth
// now; the server's testing/nes-palette.ts re-exports this same table.)
//
// Naming convention. For each of ten "hue" columns we expose four
// brightness rows in the master palette, named DARK_HUE / HUE /
// LIGHT_HUE / PALE_HUE. Greyscale gets its own five names. We do not
// expose every NES index — the columns we skip ($02 "darker blue",
// $05 "dark pink-red", $0D-$0F blacks beyond $0F, etc.) are either
// duplicates or hard to name distinctly. Authors who need them can
// still write the raw 24-bit value.
// NesJs master palette — 64 entries, each an [r, g, b] triple, row-major
// exactly as in pipu.js. Exported as the single shared source: the server
// re-encode tier imports this rather than keeping its own copy.
export const NES_MASTER_PALETTE = [
    [101, 101, 101], [0, 45, 105], [19, 31, 127], [60, 19, 124], [96, 11, 98], [115, 10, 55], [113, 15, 7], [90, 26, 0], [52, 40, 0], [11, 52, 0], [0, 60, 0], [0, 61, 16], [0, 56, 64], [0, 0, 0], [0, 0, 0], [0, 0, 0],
    [174, 174, 174], [15, 99, 179], [64, 81, 208], [120, 65, 204], [167, 54, 169], [192, 52, 112], [189, 60, 48], [159, 74, 0], [109, 92, 0], [54, 109, 0], [7, 119, 4], [0, 121, 61], [0, 114, 125], [0, 0, 0], [0, 0, 0], [0, 0, 0],
    [254, 254, 255], [93, 179, 255], [143, 161, 255], [200, 144, 255], [247, 133, 250], [255, 131, 192], [255, 139, 127], [239, 154, 73], [189, 172, 44], [133, 188, 47], [85, 199, 83], [60, 201, 140], [62, 194, 205], [78, 78, 78], [0, 0, 0], [0, 0, 0],
    [254, 254, 255], [188, 223, 255], [209, 216, 255], [232, 209, 255], [251, 205, 253], [255, 204, 229], [255, 207, 202], [248, 213, 180], [228, 220, 168], [204, 227, 169], [185, 232, 184], [174, 232, 208], [175, 229, 234], [182, 182, 182], [0, 0, 0], [0, 0, 0],
];
// Decode one NES master index (0x00-0x3F) into its 8-bit RGB triple from the
// NesJs core table the codegen constants use. Exposed for tooling — the
// devtools gallery uses it to pick a luminance-pleasing default color-cycle —
// so a sprite's gallery shade and its eventual on-screen color derive from one
// palette source.
export function nesMasterIndexToRgb(nesIndex) {
    const [r, g, b] = NES_MASTER_PALETTE[nesIndex & 0x3f] ?? [0, 0, 0];
    return { r, g, b };
}
// The NES master palette has many indices that resolve to the SAME RGB —
// ten of the 64 entries are pure black ($0D-$0F, $1D-$1F, $2E-$2F, $3E-$3F),
// and $20/$30 are both [254,254,255]. Carrying those as distinct colors
// would litter exported meta-palettes with black1/black2/black3 and split a
// shared re-encode palette into redundant slots. `canonicalNesIndex` flattens
// each index to ONE representative of its exact-RGB equivalence class — the
// HIGHEST index in the class — so two indices that paint the same pixel are
// treated as the same color everywhere downstream (variant dedup, export,
// shared-palette merge). Derived from NES_MASTER_PALETTE so it stays correct
// if the core table is ever re-copied. Purely a relabel of identical colors —
// it never changes what any pixel renders as.
const CANONICAL_NES_INDEX = (() => {
    // Highest index seen for each packed RGB key.
    const repForRgb = new Map();
    for (let i = 0; i < NES_MASTER_PALETTE.length; i++) {
        const [r, g, b] = NES_MASTER_PALETTE[i];
        const key = (r << 16) | (g << 8) | b;
        const prev = repForRgb.get(key);
        if (prev === undefined || i > prev)
            repForRgb.set(key, i);
    }
    const out = new Array(64);
    for (let i = 0; i < 64; i++) {
        const [r, g, b] = NES_MASTER_PALETTE[i] ?? [0, 0, 0];
        out[i] = repForRgb.get((r << 16) | (g << 8) | b) ?? i;
    }
    return out;
})();
export function canonicalNesIndex(nesIndex) {
    return CANONICAL_NES_INDEX[nesIndex & 0x3f];
}
// Convert one NES master index into Munos's 0xAARRGGBB (with full alpha).
function nesIndexToMunosColor(nesIndex) {
    const { r, g, b } = nesMasterIndexToRgb(nesIndex);
    return (0xff << 24 | r << 16 | g << 8 | b) >>> 0;
}
// The curated set, ordered by hue group then brightness. Each entry's
// comment notes the original NES PPU index it came from.
export const NES_NAMED_CONSTANTS = [
    // Greyscale
    { name: "NES_BLACK", nesIndex: 0x0F },
    { name: "NES_DARK_GREY", nesIndex: 0x00 },
    { name: "NES_GREY", nesIndex: 0x2D },
    { name: "NES_LIGHT_GREY", nesIndex: 0x20 },
    { name: "NES_WHITE", nesIndex: 0x30 },
    // Blue
    { name: "NES_DARK_BLUE", nesIndex: 0x01 },
    { name: "NES_BLUE", nesIndex: 0x11 },
    { name: "NES_LIGHT_BLUE", nesIndex: 0x21 },
    { name: "NES_PALE_BLUE", nesIndex: 0x31 },
    // Purple
    { name: "NES_DARK_PURPLE", nesIndex: 0x03 },
    { name: "NES_PURPLE", nesIndex: 0x13 },
    { name: "NES_LIGHT_PURPLE", nesIndex: 0x23 },
    { name: "NES_PALE_PURPLE", nesIndex: 0x33 },
    // Magenta
    { name: "NES_DARK_MAGENTA", nesIndex: 0x04 },
    { name: "NES_MAGENTA", nesIndex: 0x14 },
    { name: "NES_LIGHT_MAGENTA", nesIndex: 0x24 },
    { name: "NES_PALE_MAGENTA", nesIndex: 0x34 },
    // Red
    { name: "NES_DARK_RED", nesIndex: 0x06 },
    { name: "NES_RED", nesIndex: 0x16 },
    { name: "NES_LIGHT_RED", nesIndex: 0x26 },
    { name: "NES_PALE_RED", nesIndex: 0x36 },
    // Orange
    { name: "NES_DARK_ORANGE", nesIndex: 0x07 },
    { name: "NES_ORANGE", nesIndex: 0x17 },
    { name: "NES_LIGHT_ORANGE", nesIndex: 0x27 },
    { name: "NES_PALE_ORANGE", nesIndex: 0x37 },
    // Yellow (col 8 — dark entries trend olive-ish; consistent naming wins)
    { name: "NES_DARK_YELLOW", nesIndex: 0x08 },
    { name: "NES_YELLOW", nesIndex: 0x18 },
    { name: "NES_LIGHT_YELLOW", nesIndex: 0x28 },
    { name: "NES_PALE_YELLOW", nesIndex: 0x38 },
    // Lime (col 9 — yellow-green between yellow and green)
    { name: "NES_DARK_LIME", nesIndex: 0x09 },
    { name: "NES_LIME", nesIndex: 0x19 },
    { name: "NES_LIGHT_LIME", nesIndex: 0x29 },
    { name: "NES_PALE_LIME", nesIndex: 0x39 },
    // Green
    { name: "NES_DARK_GREEN", nesIndex: 0x0A },
    { name: "NES_GREEN", nesIndex: 0x1A },
    { name: "NES_LIGHT_GREEN", nesIndex: 0x2A },
    { name: "NES_PALE_GREEN", nesIndex: 0x3A },
    // Teal (col B — blue-green)
    { name: "NES_DARK_TEAL", nesIndex: 0x0B },
    { name: "NES_TEAL", nesIndex: 0x1B },
    { name: "NES_LIGHT_TEAL", nesIndex: 0x2B },
    { name: "NES_PALE_TEAL", nesIndex: 0x3B },
    // Cyan
    { name: "NES_DARK_CYAN", nesIndex: 0x0C },
    { name: "NES_CYAN", nesIndex: 0x1C },
    { name: "NES_LIGHT_CYAN", nesIndex: 0x2C },
    { name: "NES_PALE_CYAN", nesIndex: 0x3C },
];
// Generate a Munos source preamble that declares every entry above as
// a global `const` of type u32. The result is intended to be prepended
// to user source before lexing. The returned `lineCount` is the number
// of newline characters in the preamble — used by the compile driver
// to translate diagnostic line numbers back to user-source coordinates.
export function nesConstantsPreamble() {
    // Build one declaration per line, in a deterministic order. Width-pad
    // the name so the values line up — purely cosmetic, harmless for the
    // parser.
    const longestName = NES_NAMED_CONSTANTS
        .reduce((m, c) => Math.max(m, c.name.length), 0);
    const lines = [];
    lines.push("// Auto-injected NES master palette constants. See packages/munos/src/codegen/nes-palette.ts.");
    for (const { name, nesIndex } of NES_NAMED_CONSTANTS) {
        const value = nesIndexToMunosColor(nesIndex);
        const hex = "0x" + value.toString(16).padStart(8, "0");
        const pad = " ".repeat(longestName - name.length);
        const idxHex = "0x" + nesIndex.toString(16).padStart(2, "0").toUpperCase();
        lines.push(`const ${name}${pad}: u32 = ${hex}  // PPU ${idxHex}`);
    }
    // Terminate with one extra newline so the user's first line starts
    // cleanly on a fresh row.
    const source = lines.join("\n") + "\n";
    // lineCount = number of newlines we just inserted. Equivalently:
    // lines.length (each entry contributes exactly one \n via join + the
    // trailing \n). Spelled out for the reader.
    const lineCount = lines.length;
    return { source, lineCount };
}
//# sourceMappingURL=nes-palette.js.map